import { ActionIcon, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconBrandDocker,
    IconCheck,
    IconCopy,
    IconExclamationCircle,
    IconPackages,
    IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { RepositoryResponse } from '@xver/apis/v1/sammeln/repositories/model';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { copyToClipboard } from '@xver/utils/clipboard';
import { formatDate } from '@xver/utils/date';
import { isNetworkError } from '@xver/libs/connectionNotifier';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PROJECT_QUERY_KEY, useProjectQuery } from '../hooks/useProjectQuery';
import { REPOSITORIES_QUERY_KEY, useRepositoriesQuery } from '../hooks/useRepositoriesQuery';

interface RepositoriesTabPanelProps {
    projectIdOrName: string;
}

const REGISTRY_HOST = 'artifact.xver.cloud';

function stripProjectPrefix(repoName: string, projectName: string): string {
    if (!repoName) return repoName;
    const prefix = `${projectName}/`;
    return repoName.startsWith(prefix) ? repoName.slice(prefix.length) : repoName;
}

export default function RepositoriesTabPanel({ projectIdOrName }: RepositoriesTabPanelProps) {
    const { t, i18n } = useTranslation('artifactRegistry');
    const queryClient = useQueryClient();
    const { data: project } = useProjectQuery(projectIdOrName);
    const {
        data: repositoriesData,
        isPending,
        isError,
        error,
        refetch,
    } = useRepositoriesQuery(projectIdOrName);

    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [pendingDelete, setPendingDelete] = useState<RepositoryResponse | null>(null);

    const repositories = useMemo(() => repositoriesData?.repositories ?? [], [repositoriesData]);

    const hasShownError = useRef(false);
    useEffect(() => {
        if (isError && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('repositories.toast.loadErrorTitle', 'Failed to load repositories'),
                message:
                    (error as { message?: string } | null)?.message ??
                    t('repositories.toast.loadErrorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        }
        if (!isError) {
            hasShownError.current = false;
        }
    }, [isError, error, t]);

    const projectName = project?.name ?? projectIdOrName;

    const deleteMutation = useMutation({
        mutationFn: (repo: RepositoryResponse) => {
            const shortName = stripProjectPrefix(repo.name, projectName);
            return Endpoints.v1.Sammeln.Repositories.deleteRepository(projectIdOrName, shortName);
        },
        onSuccess: async () => {
            notifications.show({
                title: t('repositories.toast.deleteTitle', 'Repository deleted'),
                message: t(
                    'repositories.toast.deleteMessage',
                    'The repository and its artifacts were removed.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({
                queryKey: [REPOSITORIES_QUERY_KEY, projectIdOrName],
            });
            await queryClient.invalidateQueries({
                queryKey: [PROJECT_QUERY_KEY, projectIdOrName],
            });
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('repositories.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('repositories.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleCopyPullCommand = async (repo: RepositoryResponse) => {
        const shortName = stripProjectPrefix(repo.name, projectName);
        const command = `docker pull ${REGISTRY_HOST}/${projectName}/${shortName}:latest`;
        try {
            await copyToClipboard(command);
            notifications.show({
                title: t('repositories.toast.copyTitle', 'Pull command copied'),
                message: command,
                icon: <IconCheck size={18} color="#22c55e" />,
            });
        } catch {
            notifications.show({
                title: t('repositories.toast.copyErrorTitle', 'Copy failed'),
                message: t(
                    'repositories.toast.copyErrorMessage',
                    'Clipboard access is not available in this context.'
                ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        }
    };

    const handleDelete = (repo: RepositoryResponse) => {
        setPendingDelete(repo);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteMutation.mutate(pendingDelete, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
            },
        });
    };

    const columns: DataTableColumn<RepositoryResponse>[] = [
        {
            key: 'name',
            label: t('repositories.columns.name', 'Name'),
            render: (repo) => {
                const shortName = stripProjectPrefix(repo.name, projectName);
                const command = `docker pull ${REGISTRY_HOST}/${projectName}/${shortName}:latest`;
                return (
                    <div className="flex flex-col gap-1">
                        <span className="text-base font-medium text-fg">{shortName}</span>
                        <code className="text-sm text-fg/68 font-mono truncate max-w-105">
                            {command}
                        </code>
                    </div>
                );
            },
        },
        {
            key: 'artifact_count',
            label: t('repositories.columns.artifacts', 'Artifacts'),
            render: (repo) => (
                <span className="text-base text-fg/78">
                    {(repo.artifact_count ?? 0).toLocaleString()}
                </span>
            ),
        },
        {
            key: 'pull_count',
            label: t('repositories.columns.pulls', 'Pulls'),
            render: (repo) => (
                <span className="text-base text-fg/78">
                    {(repo.pull_count ?? 0).toLocaleString()}
                </span>
            ),
        },
        {
            key: 'update_time',
            label: t('repositories.columns.updated', 'Last update'),
            render: (repo) => (
                <span className="text-xs text-fg/68">
                    {repo.update_time ? formatDate(repo.update_time, i18n.language, t) : '-'}
                </span>
            ),
        },
    ];

    const pendingShortName = pendingDelete
        ? stripProjectPrefix(pendingDelete.name, projectName)
        : '';

    return (
        <>
            <DialogBox
                label={t('repositories.title', 'Repositories')}
                description={t(
                    'repositories.description',
                    'Container repositories stored in this project.'
                )}
                rightDescription={
                    <span className="inline-flex items-center gap-2 text-sm text-fg/68">
                        <IconBrandDocker size={22} />
                        {REGISTRY_HOST}/{projectName}
                    </span>
                }
            >
                <DataTable
                    caption={t('repositories.title', 'Repositories')}
                    columns={columns}
                    rows={repositories}
                    isLoading={isPending}
                    rowKey={(r) => r.id}
                    emptyMessage={
                        isError ? (
                            <div className="w-full flex flex-col items-center justify-center gap-2 py-6">
                                <IconExclamationCircle
                                    size={28}
                                    stroke={1.25}
                                    className="text-[#eb5757]"
                                />
                                <span className="text-sm text-fg/68">
                                    {t(
                                        'repositories.errorState',
                                        'We could not load the repository list.'
                                    )}
                                </span>
                                <Button variant="secondary" onClick={() => refetch()}>
                                    {t('repositories.retry', 'Retry')}
                                </Button>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col items-center justify-center gap-2 py-6">
                                <IconPackages size={28} stroke={1.25} className="text-fg/40" />
                                <span className="text-sm text-fg/68">
                                    {t(
                                        'repositories.empty',
                                        'No repositories yet. Push an image to your project to see it here.'
                                    )}
                                </span>
                            </div>
                        )
                    }
                    actions={(repo) => (
                        <div className="flex flex-row justify-end items-center gap-1">
                            <Tooltip
                                label={t('repositories.actions.copy', 'Copy pull command')}
                                withArrow
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void handleCopyPullCommand(repo);
                                    }}
                                    aria-label={t(
                                        'repositories.actions.copyAria',
                                        'Copy pull command'
                                    )}
                                >
                                    <IconCopy size={16} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip
                                label={t('repositories.actions.delete', 'Delete repository')}
                                withArrow
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(repo);
                                    }}
                                    disabled={deleteMutation.isPending}
                                    aria-label={t(
                                        'repositories.actions.deleteAria',
                                        'Delete repository'
                                    )}
                                >
                                    <IconTrash size={16} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    )}
                />
            </DialogBox>

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (deleteMutation.isPending) return;
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('repositories.confirm.deleteTitle', 'Delete repository?')}
                message={t('repositories.confirm.deleteMessage', {
                    shortName: pendingShortName,
                    defaultValue:
                        '"{{shortName}}" and all of its artifacts will be permanently removed.',
                })}
                confirmLabel={t('repositories.confirm.deleteLabel', 'Delete repository')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </>
    );
}
